/*
 * Copyright Mimic Networks, Inc. 2025.
 */
import { BugOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import { TenantReleaseListItem } from '@/client';
import { useTenantReleaseByIdQuery } from '@/hooks/query/useTenantReleaseByIdQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Button } from '@/primitives/Button';
import { Card } from '@/primitives/Card';
import { Collapse } from '@/primitives/Collapse';
import { Container } from '@/primitives/Container';
import { Flex } from '@/primitives/Flex';
import { OsIcon } from '@/primitives/Icons';
import { Select } from '@/primitives/Select';
import { Skeleton } from '@/primitives/Skeleton';
import { Text } from '@/primitives/Text';
import { Tooltip } from '@/primitives/Tooltip';

import { WindowsReleaseInstructions } from './WindowsReleaseInstructions';
import { WindowsSystemRequirements } from './WindowsSystemRequirements';

import './DownloadRelease.css';

export type DownloadReleaseProps = {
  osName: 'windows' | 'linux';
  tenantReleases: TenantReleaseListItem[];
};

export function DownloadRelease({ osName, tenantReleases }: DownloadReleaseProps) {
  const { t } = useMimicTranslation('downloadNode');
  const [selectedVersion, setSelectedVersion] = useState<TenantReleaseListItem | null>(null);

  const { data: tenantReleaseDetails } = useTenantReleaseByIdQuery({ releaseId: selectedVersion?.id ?? '' });
  const signedUrl = tenantReleaseDetails?.data.packageUrl ?? null;

  useEffect(() => {
    setSelectedVersion(tenantReleases[0]);
  }, [setSelectedVersion, tenantReleases]);

  const renderNodeVersionSelect = () => {
    return <div className="download-release-select-label">{t(`${osName}.viewOtherVersions`)}</div>;
  };

  const getVersionFlavor = (release: TenantReleaseListItem) => {
    return release.packageType?.toLowerCase() || '';
  };

  const renderVersionLabel = (release: TenantReleaseListItem) => {
    const flavor = getVersionFlavor(release);
    return (
      <Flex gap="xs" align="center">
        <span>{release.version}</span>
        {release.buildNumber && (
          <span
            style={{
              fontSize: '10px',
              backgroundColor: 'rgba(0, 0, 0, 0.06)',
              borderRadius: '10px',
              padding: '1px 6px',
              color: 'rgba(0, 0, 0, 0.65)',
            }}
          >
            +{release.buildNumber}
          </span>
        )}
        {flavor && (
          <span
            style={{
              fontSize: '10px',
              backgroundColor: 'rgba(0, 0, 0, 0.06)',
              borderRadius: '10px',
              padding: '1px 6px',
              color: 'rgba(0, 0, 0, 0.65)',
            }}
          >
            {flavor}
          </span>
        )}
        {release.debug && (
          <Tooltip title="Debug build">
            <BugOutlined style={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.65)' }} />
          </Tooltip>
        )}
      </Flex>
    );
  };

  if (!selectedVersion) {
    return <Skeleton loading />;
  }

  return (
    <Container>
      <div className="download-release-content">
        <Card padding="md">
          <div className="download-release-details">
            <div className="download-release-label">{t('version')}:</div>
            <div>
              <Flex gap="xs" align="center">
                <span>{selectedVersion.version}</span>
                {selectedVersion.buildNumber && (
                  <span
                    style={{
                      fontSize: '10px',
                      backgroundColor: 'rgba(0, 0, 0, 0.06)',
                      borderRadius: '10px',
                      padding: '1px 6px',
                      color: 'rgba(0, 0, 0, 0.65)',
                    }}
                  >
                    +{selectedVersion.buildNumber}
                  </span>
                )}
                {getVersionFlavor(selectedVersion) && (
                  <span
                    style={{
                      fontSize: '10px',
                      backgroundColor: 'rgba(0, 0, 0, 0.06)',
                      borderRadius: '10px',
                      padding: '1px 6px',
                      color: 'rgba(0, 0, 0, 0.65)',
                    }}
                  >
                    {getVersionFlavor(selectedVersion)}
                  </span>
                )}
                {selectedVersion.debug && (
                  <Tooltip title="Debug build">
                    <BugOutlined style={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.65)' }} />
                  </Tooltip>
                )}
              </Flex>
            </div>

            <div className="download-release-label">{t('architecture')}:</div>
            <div>{selectedVersion.architecture}</div>

            <div className="download-release-label">{t('platform')}:</div>
            <div>
              <Flex gap="xs" align="center">
                <OsIcon name={selectedVersion.targetOs as 'windows' | 'linux'} />
                <span style={{ textTransform: 'capitalize' }}>{selectedVersion.targetOs}</span>
              </Flex>
            </div>

            <div className="download-release-label">{t('package')}:</div>
            <div>
              <Text mono copyable>
                {selectedVersion.package || '-'}
              </Text>
            </div>

            <div className="download-release-label">{t('fileSize')}:</div>
            <div>{((selectedVersion.packageSizeBytes ?? 0) / 1024 / 1024).toFixed(2)} MB</div>

            <div className="download-release-label">{t('hash')}:</div>
            <div>
              <Text data-testid="download-release-hash" mono copyable>
                {selectedVersion.packageSha256 || '-'}
              </Text>
            </div>

            <div className="download-release-label">{t('releaseDate')}:</div>
            <div>{dayjs(selectedVersion.dateReleased).format('D MMM YYYY')}</div>
          </div>
          <div className="download-release-actions">
            {!signedUrl && (
              <Button type="primary" loading className="download-release-button">
                {t('pleaseWait')}
              </Button>
            )}
            {!!signedUrl && (
              <a href={signedUrl}>
                <Button type="primary" className="download-release-button">
                  <Flex gap="xs" align="center" justify="center" full>
                    <span>{t('download', { version: selectedVersion.version })}</span>
                    {selectedVersion.buildNumber && (
                      <span
                        style={{
                          fontSize: '10px',
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          borderRadius: '10px',
                          padding: '1px 6px',
                          color: 'rgba(255, 255, 255, 0.9)',
                        }}
                      >
                        +{selectedVersion.buildNumber}
                      </span>
                    )}
                    {selectedVersion.debug && (
                      <Tooltip title="Debug build">
                        <BugOutlined style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.9)' }} />
                      </Tooltip>
                    )}
                  </Flex>
                </Button>
              </a>
            )}
            <Select
              className="download-version-select"
              showSearch
              options={tenantReleases.map((release) => ({
                ...release,
                label: renderVersionLabel(release),
                value: release.id,
                searchValue:
                  `${release.version} ${release.buildNumber || ''} ${release.packageType || ''}`.toLowerCase(),
              }))}
              onChange={(value) => {
                const selectedRelease = tenantReleases.find((release) => release.id === value);
                setSelectedVersion(selectedRelease ?? null);
              }}
              filterOption={(input, option) => {
                const searchValue = (option as { searchValue?: string })?.searchValue || '';
                return searchValue.includes(input.toLowerCase());
              }}
              data-testid="node-version-select"
              value={{ id: 123 }}
              labelRender={renderNodeVersionSelect}
            />
          </div>
        </Card>
        {osName === 'windows' && (
          <Collapse
            bordered={false}
            items={[
              {
                key: 'system-requirements',
                label: <Text strong>{t('systemRequirements.title')}</Text>,
                children: <WindowsSystemRequirements />,
              },
              {
                key: 'install-instructions',
                label: <Text strong>{t('installInstructions.title')}</Text>,
                children: <WindowsReleaseInstructions packageType={selectedVersion.packageType ?? 'msi'} />,
              },
            ]}
          />
        )}
      </div>
    </Container>
  );
}
